/**
 * Utility functions for generating virtual lesson instances from lesson series
 * Similar to iOS app implementation
 */

/**
 * Optional function type to check if a date is unavailable for a location
 * Used to filter out virtual lessons during school holidays, etc.
 */
export type IsDateUnavailableChecker = (dateString: string, locationId: string | undefined | null) => boolean;

/** Attendance not yet taken — matches calendar sidebar (anything except completed/cancelled/present). */
export function isUnmarkedLessonStatus(status?: string | null): boolean {
  const normalized = (status || 'scheduled').toLowerCase().trim();
  return normalized !== 'completed' && normalized !== 'cancelled' && normalized !== 'present';
}

/**
 * Generate virtual lesson instances from lesson series for a specific date
 * @param series - Array of lesson series
 * @param date - The date to generate virtual instances for
 * @param realLessons - Array of real lesson instances (to avoid duplicates)
 * @param isDateUnavailable - Optional function to check if date is unavailable for location's calendar
 */
export function generateVirtualInstances(
  series: any[],
  date: Date,
  realLessons: any[],
  isDateUnavailable?: IsDateUnavailableChecker
): any[] {
  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const dateString = dateFormatter.format(date);
  
  const dayOfWeekNum = date.getDay(); // JavaScript format: 0 = Sunday, 1 = Monday, etc.
  // Convert to iOS format: Sunday=1, Monday=2, ..., Saturday=7
  const dayOfWeekNumIOS = dayOfWeekNum === 0 ? 1 : dayOfWeekNum + 1;
  
  // Check if date is in the past (before today)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  const isDateInPast = checkDate < today;
  
  const virtualLessons: any[] = [];
  
  for (const s of series) {
    // Skip cancelled series
    if (s.status === 'cancelled') continue;
    
    // Check if date is within series range
    if (dateString < s.startDate) continue;
    if (s.endDate && dateString > s.endDate) continue;
    
    // Only for matching day of week
    if (s.dayOfWeekNum !== dayOfWeekNumIOS) continue;
    
    // Skip if location is unavailable on this date (e.g., school holidays)
    // ONLY for today and future dates - past lessons should always show for marking
    // Real lessons created by tutor are always shown regardless
    if (!isDateInPast && isDateUnavailable && isDateUnavailable(dateString, s.locationId)) {
      continue;
    }
    
    // Check if real instance exists for this series on this date
    const hasInstance = realLessons.some((lesson) => {
      // Check if it's linked to this series by seriesId
      if (lesson.seriesId === s.id && lesson.date === dateString) {
        return true;
      }
      // Or if it matches the series pattern on this date
      if (
        lesson.date === dateString &&
        lesson.studentId === s.studentId &&
        lesson.time === s.time &&
        lesson.subject === s.subject
      ) {
        return true;
      }
      return false;
    });
    
    if (hasInstance) continue;
    
    // Create virtual lesson from series
    const virtual = {
      id: `${s.id}-virtual-${dateString}`,
      tutorId: s.tutorId,
      studentId: s.studentId,
      student: s.student,
      familyId: s.familyId,
      family: s.family,
      locationId: s.locationId,
      location: s.location,
      date: dateString,
      time: s.time,
      duration: s.duration,
      subject: s.subject,
      topic: null,
      status: 'scheduled',
      // Use customLessonFee first, then hourlyRate from series
      customLessonFee: s.customLessonFee,
      hourlyRate: s.hourlyRate || s.customLessonFee,
      notes: s.notes,
      homework: null,
      recurring: true,
      recurringEndDate: s.endDate,
      // Use the actual term event ID from the series, not the series ID
      recurringEndDateEventId: s.recurringEndDateEventId,
      seriesId: s.id,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
      // Mark as virtual for UI purposes
      isVirtual: true,
      // For calendar event format compatibility
      type: 'lesson',
      startTime: s.time,
      endTime: calculateEndTime(s.time, s.duration),
    };
    
    virtualLessons.push(virtual);
  }
  
  return virtualLessons;
}

/**
 * Generate virtual instances for past dates (for lessons needing marking)
 * @param series - Array of lesson series
 * @param fromDate - Start date for the range
 * @param toDate - End date for the range
 * @param realLessons - Array of real lesson instances (to avoid duplicates)
 * @param isDateUnavailable - Optional function to check if date is unavailable for location's calendar
 */
export function generateVirtualInstancesForPast(
  series: any[],
  fromDate: Date,
  toDate: Date,
  realLessons: any[],
  isDateUnavailable?: IsDateUnavailableChecker
): any[] {
  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  const virtualLessons: any[] = [];
  
  for (const s of series) {
    // Skip cancelled series
    if (s.status === 'cancelled') continue;
    
    const seriesStartDate = new Date(s.startDate);
    const seriesEndDate = s.endDate ? new Date(s.endDate) : toDate;
    const effectiveEndDate = seriesEndDate > toDate ? toDate : seriesEndDate;
    
    if (seriesStartDate > toDate) continue;
    
    // Find the first occurrence of this day of week on or after series start
    let firstOccurrence = new Date(seriesStartDate);
    const startDayOfWeek = firstOccurrence.getDay();
    const targetDayOfWeek = s.dayOfWeekNum === 1 ? 0 : s.dayOfWeekNum - 1; // Convert to JS format
    
    if (startDayOfWeek !== targetDayOfWeek) {
      const daysToAdd = (targetDayOfWeek - startDayOfWeek + 7) % 7;
      if (daysToAdd > 0) {
        firstOccurrence = new Date(firstOccurrence);
        firstOccurrence.setDate(firstOccurrence.getDate() + daysToAdd);
      }
    }
    
    // Generate virtual instances for each occurrence
    let currentOccurrence = new Date(firstOccurrence);
    while (currentOccurrence <= effectiveEndDate) {
      const dateString = dateFormatter.format(currentOccurrence);
      
      // NOTE: For past dates, we do NOT skip unavailable locations
      // Past lessons need to appear for attendance marking, even on holidays
      // The isDateUnavailable parameter is kept for API compatibility but ignored
      
      // Check if real instance exists
      const hasInstance = realLessons.some((lesson) => {
        // Check if it's linked to this series by seriesId
        if (lesson.seriesId === s.id && lesson.date === dateString) {
          return true;
        }
        if (
          lesson.date === dateString &&
          lesson.studentId === s.studentId &&
          lesson.time === s.time &&
          lesson.subject === s.subject
        ) {
          return true;
        }
        return false;
      });
      
      if (!hasInstance) {
        const virtual = {
          id: `${s.id}-virtual-${dateString}`,
          tutorId: s.tutorId,
          studentId: s.studentId,
          student: s.student,
          familyId: s.familyId,
          family: s.family,
          locationId: s.locationId,
          location: s.location,
          date: dateString,
          time: s.time,
          duration: s.duration,
          subject: s.subject,
          topic: null,
          status: 'scheduled',
          hourlyRate: s.customLessonFee,
          notes: s.notes,
          homework: null,
          recurring: true,
          recurringEndDate: s.endDate,
          // Use the actual term event ID from the series, not the series ID
          recurringEndDateEventId: s.recurringEndDateEventId,
          seriesId: s.id,
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          isVirtual: true,
          type: 'lesson',
          startTime: s.time,
          endTime: calculateEndTime(s.time, s.duration),
        };
        
        virtualLessons.push(virtual);
      }
      
      // Move to next week (same day of week)
      currentOccurrence = new Date(currentOccurrence);
      currentOccurrence.setDate(currentOccurrence.getDate() + 7);
    }
  }
  
  return virtualLessons;
}

/**
 * Calculate end time from start time and duration
 */
function calculateEndTime(startTime: string, duration: number): string {
  const [hours, minutes] = startTime.split(':').map(Number);
  const startDate = new Date();
  startDate.setHours(hours, minutes, 0, 0);
  const endDate = new Date(startDate.getTime() + duration * 60 * 1000);
  const endHours = String(endDate.getHours()).padStart(2, '0');
  const endMinutes = String(endDate.getMinutes()).padStart(2, '0');
  return `${endHours}:${endMinutes}`;
}

